// utils/api/DocumentEditorApi/ActivityApi.js

import { apiCall } from '../ApiUtils';

// Document Editor Activity API
const documentEditorApiCall = (endpoint, options = {}, classContext = null) => {
  const documentEditorEndpoint = `/document-editor${endpoint}`;
  return apiCall(documentEditorEndpoint, options, classContext);
};

export const activityApi = {
  /**
   * Log a student activity
   * @param {string} documentId - Document ID
   * @param {string} activityType - Type of activity
   * @param {string} description - Description of activity
   * @param {object} options - Additional options
   * @param {number} options.score - Optional score
   * @param {string} options.sessionId - Optional session ID
   * @param {number} options.durationSeconds - Optional duration in seconds
   * @param {object} options.metadata - Optional metadata
   * @param {object} classContext - Class context
   */
  logActivity: (documentId, activityType, description, options = {}, classContext = null) =>
    documentEditorApiCall('/activity/log', {
      method: 'POST',
      body: JSON.stringify({
        documentId,
        activityType,
        description,
        score: options.score,
        sessionId: options.sessionId,
        durationSeconds: options.durationSeconds,
        metadata: options.metadata || {}
      })
    }, classContext),

  /**
   * Close document and log session
   * @param {string} documentId - Document ID
   * @param {string} sessionId - Session ID from document open
   * @param {string} openTimestamp - ISO timestamp when document was opened
   * @param {object} classContext - Class context
   */
  closeDocument: (documentId, sessionId, openTimestamp, classContext = null) =>
    documentEditorApiCall('/activity/close-document', {
      method: 'POST',
      body: JSON.stringify({
        documentId,
        sessionId,
        openTimestamp
      })
    }, classContext),

  /**
   * Get activities for a specific student (instructor/TA only)
   * @param {string} studentUsername - Student username
   * @param {object} filters - Optional filters
   * @param {string} filters.documentId - Filter by document
   * @param {string} filters.activityType - Filter by activity type
   * @param {number} filters.limit - Limit number of results
   * @param {object} classContext - Class context
   */
  getStudentActivities: (studentUsername, filters = {}, classContext = null) => {
    const params = new URLSearchParams();
    if (filters.documentId) params.append('documentId', filters.documentId);
    if (filters.activityType) params.append('activityType', filters.activityType);
    if (filters.limit) params.append('limit', filters.limit.toString());

    const queryString = params.toString();
    return documentEditorApiCall(
      `/activity/student/${studentUsername}${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' },
      classContext
    );
  },

  /**
   * Get all activities for a specific document (instructor/TA only)
   * @param {string} documentId - Document ID
   * @param {object} filters - Optional filters
   * @param {string} filters.studentUsername - Filter by student
   * @param {string} filters.activityType - Filter by activity type
   * @param {object} classContext - Class context
   */
  getDocumentActivities: (documentId, filters = {}, classContext = null) => {
    const params = new URLSearchParams();
    if (filters.studentUsername) params.append('studentUsername', filters.studentUsername);
    if (filters.activityType) params.append('activityType', filters.activityType);

    const queryString = params.toString();
    return documentEditorApiCall(
      `/activity/document/${documentId}${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' },
      classContext
    );
  },

  /**
   * Save student ERD work for an erd:savable snippet.
   * Stored as an erd_work_save activity so it persists across sessions.
   */
  saveErdWork: (documentId, snippetId, inputText, svgContent, classContext = null) =>
    documentEditorApiCall('/activity/log', {
      method: 'POST',
      body: JSON.stringify({
        documentId,
        activityType: 'erd_work_save',
        description: 'Student saved ERD work',
        metadata: { snippetId, inputText, svgContent }
      })
    }, classContext),

  /**
   * Load the most recent saved ERD work for an erd:savable snippet.
   * Returns { success, inputText } or { success: false }.
   */
  loadErdWork: (snippetId, documentId, classContext = null) => {
    const params = new URLSearchParams({ snippetId, documentId });
    return documentEditorApiCall(
      `/activity/erd-work?${params.toString()}`,
      { method: 'GET' },
      classContext
    );
  },

  /**
   * Get AI Guide MCQ history for a section.
   * Students see their own records; educators see all (optionally filtered by student).
   * @param {string|null} sectionId - Section ID, or null for root/course-level
   * @param {object} filters - { studentUsername, limit }
   * @param {object} classContext
   */
  getAiGuideHistory: (sectionId = null, filters = {}, classContext = null) => {
    const params = new URLSearchParams();
    if (sectionId) params.append('sectionId', sectionId);
    if (filters.studentUsername) params.append('studentUsername', filters.studentUsername);
    if (filters.limit) params.append('limit', String(filters.limit));
    const queryString = params.toString();
    return documentEditorApiCall(
      `/activity/ai-guide-history${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' },
      classContext
    );
  }
};

